import { FormEvent, useEffect, useId, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { api } from "../../api/client";
import { HerdDepartureBadge } from "../../components/breeding/HerdDepartureBadge";
import { AlertBanner } from "../../components/feedback/AlertBanner";
import { EarTag, useFormatEarTag } from "../../components/tags/EarTag";
import { Field } from '../../components/forms/Field';
import { SeasonMemberPicker, type SeasonMemberDraft } from "../../components/forms/SeasonMemberPicker";
import { FormModal } from '../../components/modals/FormModal';
import { BirthsSubnav } from '../../components/subnav/BirthsSubnav';
import { ResponsiveTable } from '../../components/ui/ResponsiveTable';
import { apiError } from "../../lib/apiError";
import { formatDate, todayIso } from "../../lib/format";
import { passwordFieldProps, useFilterFieldProps } from "../../lib/inputPrivacy";
import { SIRE_COLORS, sireColorLabel } from "../../lib/species/sireColors";
import {
  AUTO_PLACEHOLDER,
  filterItemsByTagQuery,
  MANUAL_PREFIX,
  protectManualPrefixValue,
  typedTagFilterQuery,
  type TagInputMode,
} from "../../lib/tagVerificationInput";
import type {
  BreedingMating,
  BreedingSeason,
  BreedingSeasonDetail,
  EligibleAnimal,
} from "../../types";

type MatingFilter = "all" | "mated" | "unmated";
type TagSearchFocus = "idle" | "rams" | "ewes" | "both" | "none";
type BulkSireMode = "shared" | "per_ewe";

type PerEweSireChoice = {
  sire_tag: string;
  uncertain: boolean;
};

function polishCount(n: number, one: string, few: string, many: string): string {
  if (n === 1) {
    return one;
  }
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) {
    return few;
  }
  return many;
}

export function BreedingPage() {
  const formatTag = useFormatEarTag();
  const [searchParams, setSearchParams] = useSearchParams();
  const fieldNs = useId().replace(/:/g, "");
  const [seasons, setSeasons] = useState<BreedingSeason[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [detail, setDetail] = useState<BreedingSeasonDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showMembers, setShowMembers] = useState(false);
  const [eligible, setEligible] = useState<EligibleAnimal[]>([]);
  const [selectedEweTags, setSelectedEweTags] = useState<string[]>([]);
  const [matingTargets, setMatingTargets] = useState<string[] | null>(null);
  const [matingDate, setMatingDate] = useState(todayIso());
  const [matingRam, setMatingRam] = useState("");
  const [matingUncertain, setMatingUncertain] = useState(false);
  const [bulkSireMode, setBulkSireMode] = useState<BulkSireMode>("shared");
  const [perEweSires, setPerEweSires] = useState<Record<string, PerEweSireChoice>>({});
  const [matingPassword, setMatingPassword] = useState("");
  const [editStartDate, setEditStartDate] = useState(todayIso());
  const [editGestationDays, setEditGestationDays] = useState("150");
  const [editNote, setEditNote] = useState("");
  const [tagQuery, setTagQuery] = useState(MANUAL_PREFIX);
  const [tagSearchMode, setTagSearchMode] = useState<TagInputMode>("manual");
  const [matingFilter, setMatingFilter] = useState<MatingFilter>("all");
  const tagFilterProps = useFilterFieldProps("breeding_tag");

  function closeMembers() {
    setShowMembers(false);
  }

  function closeMatingModal() {
    setMatingTargets(null);
    setMatingPassword("");
    setBulkSireMode("shared");
    setPerEweSires({});
  }

  function buildPerEweChoices(tags: string[]): Record<string, PerEweSireChoice> {
    const next: Record<string, PerEweSireChoice> = {};
    for (const tag of tags) {
      const existing = (detail?.matings ?? []).find((mating) => mating.dam_tag === tag);
      next[tag] = {
        sire_tag: existing?.sire_tag || "",
        uncertain: existing ? existing.sire_uncertain || !existing.sire_tag : false,
      };
    }
    return next;
  }

  function openMatingModal(eweTag: string) {
    const existing = (detail?.matings ?? []).find((mating) => mating.dam_tag === eweTag);
    setMatingTargets([eweTag]);
    setMatingDate(existing?.mated_at || todayIso());
    setMatingUncertain(existing ? existing.sire_uncertain || !existing.sire_tag : false);
    setMatingRam(existing?.sire_tag || "");
    setBulkSireMode("shared");
    setPerEweSires({});
    setMatingPassword("");
    setError(null);
  }

  function openBulkMatingModal() {
    if (selectedEweTags.length === 0) {
      return;
    }
    const existing = selectedEweTags.map((tag) =>
      (detail?.matings ?? []).find((mating) => mating.dam_tag === tag),
    );
    const first = existing[0];
    const sameDate =
      first &&
      existing.every((mating) => mating && mating.mated_at === first.mated_at);
    const sameSire =
      first &&
      existing.every(
        (mating) =>
          mating &&
          mating.sire_tag === first.sire_tag &&
          Boolean(mating.sire_uncertain || !mating.sire_tag) ===
            Boolean(first.sire_uncertain || !first.sire_tag),
      );
    setMatingTargets([...selectedEweTags]);
    setMatingDate(sameDate && first ? first.mated_at : todayIso());
    setMatingUncertain(
      sameSire && first ? first.sire_uncertain || !first.sire_tag : false,
    );
    setMatingRam(sameSire && first?.sire_tag ? first.sire_tag : "");
    setBulkSireMode(sameSire ? "shared" : "per_ewe");
    setPerEweSires(buildPerEweChoices(selectedEweTags));
    setMatingPassword("");
    setError(null);
  }

  function updatePerEweSire(tag: string, patch: Partial<PerEweSireChoice>) {
    setPerEweSires((prev) => {
      const current = prev[tag] ?? { sire_tag: "", uncertain: false };
      const next = { ...current, ...patch };
      if (patch.uncertain === true) {
        next.sire_tag = "";
      }
      return { ...prev, [tag]: next };
    });
  }

  function toggleEweSelection(tag: string) {
    setSelectedEweTags((prev) =>
      prev.includes(tag) ? prev.filter((item) => item !== tag) : [...prev, tag],
    );
  }

  async function loadSeasons(preferId?: number | null) {
    const result = await api.GET("/api/breeding-seasons", {
      params: { query: { scope: "active" } },
    });
    if (result.error) {
      setError(apiError(result.error, "Nie udało się wczytać stanówki."));
      return;
    }
    const list = (result.data as BreedingSeason[]) ?? [];
    setSeasons(list);
    const fromUrl = Number(searchParams.get("seasonId") || 0) || null;
    const explicit =
      preferId && preferId > 0 ? preferId : fromUrl && fromUrl > 0 ? fromUrl : null;
    const nextId =
      explicit ??
      (selectedId && list.some((season) => season.id === selectedId)
        ? selectedId
        : (list[0]?.id ?? null));
    setSelectedId(nextId);
    if (nextId) {
      await loadDetail(nextId);
    } else {
      setDetail(null);
    }
  }

  async function loadDetail(seasonId: number) {
    const result = await api.GET("/api/breeding-seasons/{season_id}", {
      params: { path: { season_id: seasonId } },
    });
    if (result.error) {
      setError(apiError(result.error, "Nie udało się wczytać grupy."));
      return;
    }
    setError(null);
    setDetail(result.data as BreedingSeasonDetail);
  }

  useEffect(() => {
    const fromUrl = Number(searchParams.get("seasonId"));
    void loadSeasons(Number.isFinite(fromUrl) && fromUrl > 0 ? fromUrl : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rams = useMemo(
    () => (detail?.members ?? []).filter((member) => member.role === "sire"),
    [detail],
  );
  const ewes = useMemo(
    () => (detail?.members ?? []).filter((member) => member.role === "dam"),
    [detail],
  );
  const matingEweMember = useMemo(
    () =>
      matingTargets?.length === 1
        ? ewes.find((ewe) => ewe.tag === matingTargets[0])
        : undefined,
    [ewes, matingTargets],
  );
  const matingByEwe = useMemo(() => {
    const map = new Map<string, BreedingMating>();
    for (const mating of detail?.matings ?? []) {
      map.set(mating.dam_tag, mating);
    }
    return map;
  }, [detail]);

  useEffect(() => {
    setSelectedEweTags([]);
  }, [detail?.id]);

  const activeTagQuery = useMemo(
    () => typedTagFilterQuery(tagSearchMode, tagQuery),
    [tagQuery, tagSearchMode],
  );

  const matchedRams = useMemo(
    () => (activeTagQuery ? filterItemsByTagQuery(rams, activeTagQuery) : []),
    [activeTagQuery, rams],
  );

  const matchedEwesByTag = useMemo(
    () => (activeTagQuery ? filterItemsByTagQuery(ewes, activeTagQuery) : []),
    [activeTagQuery, ewes],
  );

  const tagSearchFocus = useMemo((): TagSearchFocus => {
    if (!activeTagQuery) {
      return "idle";
    }
    const hasRams = matchedRams.length > 0;
    const hasEwes = matchedEwesByTag.length > 0;
    if (hasRams && hasEwes) {
      return "both";
    }
    if (hasRams) {
      return "rams";
    }
    if (hasEwes) {
      return "ewes";
    }
    return "none";
  }, [activeTagQuery, matchedEwesByTag.length, matchedRams.length]);

  // Obie tabele zawsze widoczne. Filtrujemy tylko stronę z trafieniem;
  // druga (oraz brak wyniku) zostaje z pełnymi danymi.
  const displayedRams = useMemo(() => {
    if (tagSearchFocus === "rams" || tagSearchFocus === "both") {
      return matchedRams;
    }
    return rams;
  }, [matchedRams, rams, tagSearchFocus]);

  const displayedEwes = useMemo(() => {
    const source =
      tagSearchFocus === "ewes" || tagSearchFocus === "both" ? matchedEwesByTag : ewes;
    return source.filter((ewe) => {
      const mated = matingByEwe.has(ewe.tag);
      if (matingFilter === "mated" && !mated) {
        return false;
      }
      if (matingFilter === "unmated" && mated) {
        return false;
      }
      return true;
    });
  }, [ewes, matchedEwesByTag, matingByEwe, matingFilter, tagSearchFocus]);

  const displayedEweTags = useMemo(
    () => displayedEwes.map((ewe) => ewe.tag),
    [displayedEwes],
  );
  const allDisplayedSelected =
    displayedEweTags.length > 0 &&
    displayedEweTags.every((tag) => selectedEweTags.includes(tag));
  const someDisplayedSelected =
    !allDisplayedSelected &&
    displayedEweTags.some((tag) => selectedEweTags.includes(tag));

  function toggleSelectDisplayedEwes() {
    if (allDisplayedSelected) {
      setSelectedEweTags((prev) => prev.filter((tag) => !displayedEweTags.includes(tag)));
      return;
    }
    setSelectedEweTags((prev) => [...new Set([...prev, ...displayedEweTags])]);
  }

  const tagSearchStatus = useMemo(() => {
    switch (tagSearchFocus) {
      case "rams": {
        const label = polishCount(
          matchedRams.length,
          "Znaleziono tryka",
          `Znaleziono ${matchedRams.length} tryki`,
          `Znaleziono ${matchedRams.length} tryków`,
        );
        return `${label} — odfiltrowano listę tryków.`;
      }
      case "ewes": {
        const label = polishCount(
          matchedEwesByTag.length,
          "Znaleziono owcę",
          `Znaleziono ${matchedEwesByTag.length} owce`,
          `Znaleziono ${matchedEwesByTag.length} owiec`,
        );
        return `${label} — odfiltrowano listę owiec.`;
      }
      case "both":
        return "Znaleziono owce i tryki — odfiltrowano obie listy.";
      case "none":
        return "Nie znaleziono zwierzęcia o tym kolczyku — listy bez zmian.";
      default:
        return "Wspólne wyszukiwanie dla owiec i tryków";
    }
  }, [matchedEwesByTag.length, matchedRams.length, tagSearchFocus]);

  // Lista do wyboru zależy od daty rozpoczęcia: aktualne stado + zwierzęta,
  // które były w stadzie w tym dniu (także te, które odeszły później).
  async function loadEligible(asOf: string) {
    const query: { breed?: string; as_of?: string } = {};
    if (detail?.breed) {
      query.breed = detail.breed;
    }
    if (asOf) {
      query.as_of = asOf;
    }
    const result = await api.GET("/api/breeding-seasons/eligible-animals", {
      params: { query },
    });
    if (result.error) {
      setError(apiError(result.error, "Nie udało się wczytać zwierząt do składu."));
      return;
    }
    setEligible((result.data as EligibleAnimal[]) ?? []);
  }

  async function openMembers() {
    const startDate = detail?.start_date?.slice(0, 10) || todayIso();
    setShowMembers(true);
    setEditStartDate(startDate);
    setEditGestationDays(String(detail?.gestation_days ?? 150));
    setEditNote(detail?.note || "");
    await loadEligible(startDate);
  }

  useEffect(() => {
    if (!showMembers || !editStartDate) {
      return;
    }
    void loadEligible(editStartDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editStartDate]);

  const editMembersRequested = searchParams.get("editMembers") === "1";
  useEffect(() => {
    if (!editMembersRequested || !detail) {
      return;
    }
    void openMembers();
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("editMembers");
        return next;
      },
      { replace: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editMembersRequested, detail?.id]);

  async function saveMembers(members: SeasonMemberDraft[], password: string) {
    if (!detail) {
      return;
    }
    const gestation = Number(editGestationDays);
    if (!Number.isFinite(gestation) || gestation < 1 || gestation > 400) {
      setError("Podaj czas ciąży w dniach (1–400).");
      return;
    }

    setBusy(true);
    const updated = await api.PUT("/api/breeding-seasons/{season_id}/members", {
      params: { path: { season_id: detail.id } },
      body: {
        password: password || null,
        members,
        start_date: editStartDate || null,
        gestation_days: gestation,
        note: editNote.trim() || null,
      } as never,
    });
    setBusy(false);
    if (updated.error) {
      setError(apiError(updated.error, "Nie udało się zapisać grupy."));
      return;
    }
    closeMembers();
    setDetail(updated.data as BreedingSeasonDetail);
    await loadSeasons(detail.id);
  }

  async function recordMating(event: FormEvent) {
    event.preventDefault();
    if (!detail || !matingTargets || matingTargets.length === 0) {
      return;
    }

    const isBulk = matingTargets.length > 1;
    const usePerEwe = isBulk && bulkSireMode === "per_ewe";

    if (usePerEwe) {
      for (const tag of matingTargets) {
        const choice = perEweSires[tag];
        if (!choice) {
          setError(`Uzupełnij tryka dla owcy ${formatTag(tag)}.`);
          return;
        }
        if (!choice.uncertain && !choice.sire_tag) {
          setError(
            `Wybierz tryka dla owcy ${formatTag(tag)} albo ustaw „niepewny tryk”.`,
          );
          return;
        }
        const chosenRam = rams.find((ram) => ram.tag === choice.sire_tag);
        if (
          !choice.uncertain &&
          chosenRam?.left_herd_on &&
          matingDate > chosenRam.left_herd_on
        ) {
          setError(
            `Tryk ${formatTag(chosenRam.tag)} opuścił stado ${formatDate(chosenRam.left_herd_on)} — nie może pokryć owcy ${formatTag(tag)} później.`,
          );
          return;
        }
      }
    } else {
      if (!matingUncertain && !matingRam) {
        setError("Wybierz tryka albo ustaw tryb „Niepewny tryk”.");
        return;
      }
      const chosenRam = rams.find((ram) => ram.tag === matingRam);
      if (
        !matingUncertain &&
        chosenRam?.left_herd_on &&
        matingDate > chosenRam.left_herd_on
      ) {
        setError(
          `Tryk ${formatTag(chosenRam.tag)} opuścił stado ${formatDate(chosenRam.left_herd_on)} — nie może pokryć owcy później.`,
        );
        return;
      }
    }

    const items = usePerEwe
      ? matingTargets.map((tag) => {
          const choice = perEweSires[tag]!;
          return {
            dam_tag: tag,
            sire_tag: choice.uncertain ? null : choice.sire_tag || null,
            sire_uncertain: choice.uncertain,
          };
        })
      : matingTargets.map((tag) => ({
          dam_tag: tag,
          sire_tag: matingUncertain ? null : matingRam || null,
          sire_uncertain: matingUncertain,
        }));

    setBusy(true);
    const updated =
      matingTargets.length === 1
        ? await api.PUT("/api/breeding-seasons/{season_id}/matings/{dam_tag}", {
            params: { path: { season_id: detail.id, dam_tag: matingTargets[0]! } },
            body: {
              mated_at: matingDate,
              sire_tag: items[0]!.sire_tag,
              sire_uncertain: items[0]!.sire_uncertain,
              password: matingPassword || null,
            },
          })
        : await api.PUT("/api/breeding-seasons/{season_id}/matings", {
            params: { path: { season_id: detail.id } },
            body: {
              mated_at: matingDate,
              items,
              password: matingPassword || null,
            },
          });
    setBusy(false);
    if (updated.error) {
      setError(apiError(updated.error, "Nie udało się zapisać pokrycia."));
      return;
    }
    setDetail(updated.data as BreedingSeasonDetail);
    setSelectedEweTags([]);
    closeMatingModal();
    await loadSeasons(detail.id);
  }

  async function deleteMating() {
    if (!detail || !matingTargets || matingTargets.length !== 1) {
      return;
    }
    setBusy(true);
    const updated = await api.DELETE("/api/breeding-seasons/{season_id}/matings/{dam_tag}", {
      params: { path: { season_id: detail.id, dam_tag: matingTargets[0]! } },
      body: { password: matingPassword || null },
    });
    setBusy(false);
    if (updated.error) {
      setError(apiError(updated.error, "Nie udało się usunąć pokrycia."));
      return;
    }
    setDetail(updated.data as BreedingSeasonDetail);
    closeMatingModal();
    await loadSeasons(detail.id);
  }

  async function patchRamColor(tag: string, ramColor: string) {
    if (!detail) {
      return;
    }
    setBusy(true);
    const updated = await api.PATCH("/api/breeding-seasons/{season_id}/members/{tag}", {
      params: { path: { season_id: detail.id, tag } },
      body: {
        password: matingPassword || null,
        sire_color: ramColor || null,
      },
    });
    setBusy(false);
    if (updated.error) {
      setError(apiError(updated.error, "Nie udało się zapisać koloru tryka."));
      return;
    }
    setDetail(updated.data as BreedingSeasonDetail);
  }

  return (
    <main className="page page--breeding" data-testid="breeding-page">
      <header className="page__header">
        <p className="page__kicker">Rozród</p>
        <h1>Stanówka</h1>
      </header>

      <BirthsSubnav area="breeding" />

      <AlertBanner variant="error" message={error} onDismiss={() => setError(null)} />

      <SeasonMemberPicker
        open={showMembers}
        onClose={closeMembers}
        members={detail?.members ?? []}
        eligible={eligible}
        busy={busy}
        onSave={saveMembers}
        header={
          <section className="breeding-edit-section">
            <h4 className="breeding-edit-section__title">Parametry grupy</h4>
            <div className="form__row">
              <Field label="Data rozpoczęcia">
                <input
                  className="input"
                  type="date"
                  value={editStartDate}
                  onChange={(event) => setEditStartDate(event.target.value)}
                  data-testid="breeding-edit-start-date"
                />
              </Field>
              <Field label="Czas ciąży (dni)">
                <input
                  className="input"
                  type="number"
                  min={1}
                  max={400}
                  value={editGestationDays}
                  onChange={(event) => setEditGestationDays(event.target.value)}
                  data-testid="breeding-edit-gestation-days"
                  required
                />
              </Field>
            </div>
            <Field label={`Notatka (${editNote.length}/100)`}>
              <input
                className="input"
                value={editNote}
                maxLength={100}
                onChange={(event) => setEditNote(event.target.value)}
                data-testid="breeding-edit-note"
                placeholder="Krótki opis grupy…"
              />
            </Field>
            <p className="muted breeding-edit-section__hint">
              Planowany wykot = data pokrycia + {editGestationDays || "?"} dni.
            </p>
          </section>
        }
      />

      <div className="season-picker" data-testid="breeding-season-bar">
        {seasons.length === 0 ? (
          <p className="muted season-picker__empty">
            Brak aktywnych grup — utwórz je w{" "}
            <Link to="/reproduction">Aktywne grupy</Link>.
          </p>
        ) : (
          <label className="season-picker__field">
            <span className="season-picker__label">Grupa stanówki</span>
            <select
              className="input"
              value={selectedId ?? ""}
              aria-label="Wybierz grupę stanówki"
              data-testid="breeding-season-select"
              onChange={(event) => {
                const nextId = Number(event.target.value);
                if (!Number.isFinite(nextId)) {
                  return;
                }
                setSelectedId(nextId);
                void loadDetail(nextId);
              }}
            >
              {seasons.map((season) => (
                <option key={season.id} value={season.id}>
                  {season.name}
                  {season.breed ? ` · ${season.breed}` : ""}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      {detail ? (
        <section className="panel panel--breeding">
          <div className="panel__header">
            <div>
              <h2 className="panel__title">{detail.name}</h2>
              <p className="muted">
                {detail.breed || "Bez rasy"} · start {formatDate(detail.start_date)} · ciąża{" "}
                {detail.gestation_days ?? 150} dni · owce {detail.dam_count} · tryki{" "}
                {detail.sire_count} · pokryte {detail.mated_count}
              </p>
              {detail.note ? <p className="breeding-note">{detail.note}</p> : null}
            </div>
            <div className="panel__header-actions">
              <button type="button" className="btn btn--ghost" onClick={() => void openMembers()}>
                Edytuj grupę
              </button>
              <Link
                className="btn btn--ghost"
                to={`/reproduction/births?seasonId=${detail.birth_season_id}`}
              >
                Wykoty tej grupy
              </Link>
            </div>
          </div>

          <div className="filters filters--breeding" data-testid="breeding-tag-search">
            <select
              className="input"
              data-testid="breeding-tag-search-mode"
              value={tagSearchMode}
              aria-label="Tryb wyszukiwania kolczyka"
              onChange={(event) => {
                const next = event.target.value as TagInputMode;
                setTagSearchMode(next);
                setTagQuery(next === "manual" ? MANUAL_PREFIX : "");
              }}
            >
              <option value="manual">Ręcznie</option>
              <option value="auto">Automatycznie</option>
            </select>
            <input
              className="input"
              type="search"
              placeholder={
                tagSearchMode === "auto" ? AUTO_PLACEHOLDER : `${MANUAL_PREFIX}…`
              }
              value={tagQuery}
              onChange={(event) => {
                const value =
                  tagSearchMode === "manual"
                    ? protectManualPrefixValue(event.target.value)
                    : event.target.value;
                setTagQuery(value);
              }}
              onSelect={(event) => {
                if (tagSearchMode !== "manual") {
                  return;
                }
                const input = event.currentTarget;
                if ((input.selectionStart ?? 0) < MANUAL_PREFIX.length) {
                  input.setSelectionRange(MANUAL_PREFIX.length, MANUAL_PREFIX.length);
                }
              }}
              data-testid="breeding-tag-search-input"
              aria-label="Szukaj owcy lub tryka po kolczyku"
              aria-describedby={
                tagSearchFocus !== "idle" ? "breeding-tag-search-status" : undefined
              }
              {...tagFilterProps}
            />
            <select
              className="input"
              value={matingFilter}
              aria-label="Status krycia"
              data-testid="breeding-mating-filter-select"
              onChange={(event) => setMatingFilter(event.target.value as MatingFilter)}
            >
              <option value="all">Status krycia: wszystkie</option>
              <option value="mated">Status krycia: pokryte</option>
              <option value="unmated">Status krycia: niepokryte</option>
            </select>
          </div>
          {tagSearchFocus !== "idle" ? (
            <p
              id="breeding-tag-search-status"
              className={
                tagSearchFocus === "none"
                  ? "breeding-filters-status breeding-filters-status--empty"
                  : "breeding-filters-status breeding-filters-status--hit"
              }
              data-testid="breeding-tag-search-status"
              role="status"
            >
              {tagSearchStatus}
            </p>
          ) : null}

          {selectedEweTags.length > 0 ? (
            <div
              className="breeding-bulk-bar"
              data-testid="breeding-bulk-bar"
              role="status"
            >
              <p className="breeding-bulk-bar__text">
                Wybrane:{" "}
                <strong>
                  {polishCount(
                    selectedEweTags.length,
                    "1 owca",
                    `${selectedEweTags.length} owce`,
                    `${selectedEweTags.length} owiec`,
                  )}
                </strong>
              </p>
              <div className="actions">
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  onClick={() => setSelectedEweTags([])}
                  data-testid="breeding-bulk-clear"
                >
                  Wyczyść
                </button>
                <button
                  type="button"
                  className="btn btn--sm"
                  disabled={busy}
                  onClick={openBulkMatingModal}
                  data-testid="breeding-bulk-mating-open"
                >
                  Odnotuj pokrycie
                </button>
              </div>
            </div>
          ) : null}

          <div className="split breeding-split" data-testid="breeding-members-panels">
            <div className="breeding-panel" data-testid="breeding-rams-panel">
              <div className="breeding-panel__titlebar">
                <h3>Tryki</h3>
                <span className="muted">
                  {detail.sire_count} {polishCount(detail.sire_count, "tryk", "tryki", "tryków")}
                </span>
              </div>
              <div className="table-wrap table-wrap--breeding">
                {rams.length === 0 ? (
                  <p className="muted breeding-panel__empty">Brak tryków w składzie.</p>
                ) : displayedRams.length === 0 ? (
                  <p className="muted breeding-panel__empty">
                    Brak tryków spełniających wyszukiwanie.
                  </p>
                ) : (
                  <ResponsiveTable className="tbl" data-testid="breeding-rams-table">
                    <thead>
                      <tr>
                        <th>Kolczyk</th>
                        <th>Kolor</th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayedRams.map((ram) => (
                        <tr key={ram.tag}>
                          <td>
                            <span className="breeding-mating">
                              {ram.sire_color ? (
                                <span
                                  className={`ram-swatch ram-swatch--${ram.sire_color}`}
                                  aria-hidden="true"
                                />
                              ) : null}
                              <strong>
                                <EarTag value={ram.tag} />
                              </strong>
                              <HerdDepartureBadge
                                leftOn={ram.left_herd_on}
                                status={ram.left_herd_status}
                                extra="Nie może pokryć owcy po tej dacie."
                              />
                            </span>
                          </td>
                          <td>
                            <div className="breeding-color-picker">
                              {ram.sire_color ? (
                                <span
                                  className={`ram-swatch ram-swatch--${ram.sire_color}`}
                                  aria-hidden="true"
                                />
                              ) : (
                                <span className="ram-swatch" aria-hidden="true" />
                              )}
                              <select
                                className="input"
                                value={ram.sire_color || ""}
                                aria-label={`Kolor tryka ${formatTag(ram.tag)}`}
                                disabled={busy}
                                onChange={(event) =>
                                  void patchRamColor(ram.tag, event.target.value)
                                }
                              >
                                <option value="">Bez koloru</option>
                                {SIRE_COLORS.map((color) => (
                                  <option key={color.id} value={color.id}>
                                    {color.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </ResponsiveTable>
                )}
              </div>
            </div>
            <div className="breeding-panel" data-testid="breeding-ewes-panel">
              <div className="breeding-panel__titlebar">
                <h3>Lista owiec</h3>
                <span className="muted">
                  Pokryte {detail.mated_count}/{detail.dam_count}
                </span>
              </div>
              <div className="table-wrap table-wrap--breeding">
                {displayedEwes.length === 0 ? (
                  <p className="muted breeding-panel__empty">
                    Brak owiec spełniających kryterium.
                  </p>
                ) : (
                  <ResponsiveTable className="tbl" data-testid="breeding-ewes-table">
                    <thead>
                      <tr>
                        <th className="breeding-select-col">
                          <input
                            type="checkbox"
                            checked={allDisplayedSelected}
                            ref={(input) => {
                              if (input) {
                                input.indeterminate = someDisplayedSelected;
                              }
                            }}
                            onChange={toggleSelectDisplayedEwes}
                            aria-label="Zaznacz wszystkie widoczne owce"
                            data-testid="breeding-ewes-select-all"
                          />
                        </th>
                        <th>Kolczyk</th>
                        <th>Pokrycie</th>
                        <th>Planowany wykot</th>
                        <th>Data pokrycia</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayedEwes.map((ewe) => {
                        const mating = matingByEwe.get(ewe.tag);
                        const selected = selectedEweTags.includes(ewe.tag);
                        return (
                          <tr
                            key={ewe.tag}
                            className={selected ? "breeding-ewe-row is-selected" : undefined}
                          >
                            <td className="breeding-select-col">
                              <input
                                type="checkbox"
                                checked={selected}
                                onChange={() => toggleEweSelection(ewe.tag)}
                                aria-label={`Zaznacz owcę ${formatTag(ewe.tag)}`}
                                data-testid="breeding-ewe-select"
                              />
                            </td>
                            <td>
                              <strong>
                                <EarTag value={ewe.tag} />
                              </strong>
                              <HerdDepartureBadge
                                leftOn={ewe.left_herd_on}
                                status={ewe.left_herd_status}
                                extra={
                                  mating?.dam_lambing_blocked
                                    ? `Opuściła stado przed planowanym wykotem (${detail.gestation_days} dni od pokrycia) — nie może się wykocić.`
                                    : undefined
                                }
                              />
                            </td>
                            <td>
                              {!mating ? (
                                "—"
                              ) : mating.sire_uncertain || !mating.sire_tag ? (
                                <span className="breeding-mating breeding-mating--uncertain">
                                  Niepewny tryk
                                </span>
                              ) : (
                                <span className="breeding-mating">
                                  {mating.sire_color ? (
                                    <span
                                      className={`ram-swatch ram-swatch--${mating.sire_color}`}
                                      title={sireColorLabel(mating.sire_color)}
                                      aria-hidden="true"
                                    />
                                  ) : null}
                                  <span>
                                    <EarTag value={mating.sire_tag} />
                                  </span>
                                </span>
                              )}
                            </td>
                            <td>
                              {mating?.planned_birth_date
                                ? formatDate(mating.planned_birth_date)
                                : "—"}
                            </td>
                            <td>{mating ? formatDate(mating.mated_at) : "—"}</td>
                            <td>
                              <button
                                type="button"
                                className="btn btn--sm"
                                disabled={busy}
                                onClick={() => openMatingModal(ewe.tag)}
                                data-testid="breeding-mating-open"
                              >
                                {mating ? "Edytuj" : "Pokrycie"}
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </ResponsiveTable>
                )}
              </div>
            </div>
          </div>
        </section>
      ) : null}

      <FormModal
        open={Boolean(matingTargets && matingTargets.length > 0)}
        onClose={closeMatingModal}
        title={
          matingTargets && matingTargets.length > 1
            ? `Odnotuj pokrycie (${matingTargets.length})`
            : matingTargets?.length === 1 && matingByEwe.has(matingTargets[0]!)
              ? "Edytuj pokrycie"
              : "Odnotuj pokrycie"
        }
        size="lg"
        testId="breeding-mating-modal"
        onSubmit={recordMating}
      >
        {matingTargets && matingTargets.length > 1 ? (
          <p className="muted" data-testid="breeding-mating-bulk-summary">
            Wybrane owce:{" "}
            <strong>
              {polishCount(
                matingTargets.length,
                "1 owca",
                `${matingTargets.length} owce`,
                `${matingTargets.length} owiec`,
              )}
            </strong>
            . Wspólna data krycia; tryka możesz ustawić wspólnie albo osobno dla każdej owcy.
          </p>
        ) : (
          <p className="muted">
            Owca: <strong>{matingTargets?.[0]}</strong>
            {matingEweMember ? (
              <HerdDepartureBadge
                leftOn={matingEweMember.left_herd_on}
                status={matingEweMember.left_herd_status}
                extra={`Jeśli opuściła stado wcześniej niż ${detail?.gestation_days ?? 150} dni od pokrycia, nie może się wykocić.`}
              />
            ) : null}
          </p>
        )}
        <Field label="Data pokrycia">
          <input
            className="input"
            type="date"
            value={matingDate}
            onChange={(event) => setMatingDate(event.target.value)}
            required
            data-testid="breeding-mating-date"
          />
        </Field>
        {matingTargets && matingTargets.length > 1 ? (
          <div className="breeding-mode" role="tablist" aria-label="Tryb przypisania tryka">
            <button
              type="button"
              role="tab"
              aria-selected={bulkSireMode === "shared"}
              className={
                bulkSireMode === "shared"
                  ? "breeding-mode__btn breeding-mode__btn--active"
                  : "breeding-mode__btn"
              }
              onClick={() => setBulkSireMode("shared")}
              data-testid="breeding-bulk-sire-shared"
            >
              Ten sam tryk
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={bulkSireMode === "per_ewe"}
              className={
                bulkSireMode === "per_ewe"
                  ? "breeding-mode__btn breeding-mode__btn--active"
                  : "breeding-mode__btn"
              }
              onClick={() => {
                setBulkSireMode("per_ewe");
                setPerEweSires((prev) => {
                  const next = { ...prev };
                  for (const tag of matingTargets) {
                    if (!next[tag]) {
                      next[tag] = { sire_tag: "", uncertain: false };
                    }
                  }
                  return next;
                });
              }}
              data-testid="breeding-bulk-sire-per-ewe"
            >
              Osobno dla każdej
            </button>
          </div>
        ) : null}
        {matingTargets && matingTargets.length > 1 && bulkSireMode === "per_ewe" ? (
          <div className="breeding-per-ewe-sires" data-testid="breeding-per-ewe-sires">
            {rams.length === 0 ? (
              <p className="muted">Brak tryków w składzie — dodaj je w „Edytuj grupę”.</p>
            ) : null}
            {matingTargets.map((tag) => {
              const choice = perEweSires[tag] ?? { sire_tag: "", uncertain: false };
              return (
                <div key={tag} className="breeding-per-ewe-sires__row">
                  <div className="breeding-per-ewe-sires__ewe">
                    <strong>
                      <EarTag value={tag} />
                    </strong>
                  </div>
                  <label className="breeding-per-ewe-sires__uncertain">
                    <input
                      type="checkbox"
                      checked={choice.uncertain}
                      onChange={(event) =>
                        updatePerEweSire(tag, {
                          uncertain: event.target.checked,
                          sire_tag: event.target.checked ? "" : choice.sire_tag,
                        })
                      }
                      data-testid="breeding-per-ewe-uncertain"
                    />
                    Niepewny tryk
                  </label>
                  <select
                    className="input"
                    value={choice.uncertain ? "" : choice.sire_tag}
                    disabled={choice.uncertain || rams.length === 0}
                    aria-label={`Tryk dla owcy ${formatTag(tag)}`}
                    data-testid="breeding-per-ewe-ram"
                    onChange={(event) =>
                      updatePerEweSire(tag, {
                        sire_tag: event.target.value,
                        uncertain: false,
                      })
                    }
                    required={!choice.uncertain}
                  >
                    <option value="">Wybierz tryka…</option>
                    {rams.map((ram) => {
                      const ramGone = Boolean(
                        ram.left_herd_on && matingDate && matingDate > ram.left_herd_on,
                      );
                      return (
                        <option key={ram.tag} value={ram.tag} disabled={ramGone}>
                          {formatTag(ram.tag)}
                          {ram.sire_color ? ` · ${sireColorLabel(ram.sire_color)}` : ""}
                          {ramGone ? " (opuścił stado)" : ""}
                        </option>
                      );
                    })}
                  </select>
                </div>
              );
            })}
          </div>
        ) : (
          <>
            <div className="breeding-mode" role="tablist" aria-label="Tryb ojca">
              <button
                type="button"
                role="tab"
                aria-selected={!matingUncertain}
                className={
                  !matingUncertain
                    ? "breeding-mode__btn breeding-mode__btn--active"
                    : "breeding-mode__btn"
                }
                onClick={() => setMatingUncertain(false)}
              >
                Konkretny tryk
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={matingUncertain}
                className={
                  matingUncertain
                    ? "breeding-mode__btn breeding-mode__btn--active"
                    : "breeding-mode__btn"
                }
                onClick={() => {
                  setMatingUncertain(true);
                  setMatingRam("");
                }}
              >
                Niepewny tryk
              </button>
            </div>
            {!matingUncertain ? (
              <div>
                <p className="field__label">Wybierz tryka</p>
                {rams.length === 0 ? (
                  <p className="muted">Brak tryków w składzie — dodaj je w „Edytuj grupę”.</p>
                ) : (
                  <div className="breeding-ram-list" role="radiogroup" aria-label="Tryk">
                    {rams.map((ram) => {
                      const selected = matingRam === ram.tag;
                      const ramGone = Boolean(
                        ram.left_herd_on && matingDate && matingDate > ram.left_herd_on,
                      );
                      return (
                        <label
                          key={ram.tag}
                          className={
                            selected
                              ? "breeding-ram-option is-selected"
                              : "breeding-ram-option"
                          }
                        >
                          <input
                            type="radio"
                            name="breeding-mating-ram"
                            checked={selected}
                            disabled={ramGone}
                            onChange={() => setMatingRam(ram.tag)}
                          />
                          {ram.sire_color ? (
                            <span
                              className={`ram-swatch ram-swatch--${ram.sire_color}`}
                              title={sireColorLabel(ram.sire_color)}
                              aria-hidden="true"
                            />
                          ) : (
                            <span className="ram-swatch" aria-hidden="true" />
                          )}
                          <span>
                            <strong>
                              <EarTag value={ram.tag} />
                            </strong>
                            {ram.sire_color ? (
                              <span className="muted"> · {sireColorLabel(ram.sire_color)}</span>
                            ) : null}
                            <HerdDepartureBadge
                              leftOn={ram.left_herd_on}
                              status={ram.left_herd_status}
                            />
                            {ramGone ? (
                              <span className="herd-departure-note">
                                Tryk opuścił stado przed tą datą pokrycia.
                              </span>
                            ) : null}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : (
              <p className="muted">Ojciec pozostanie oznaczony jako niepewny.</p>
            )}
          </>
        )}
        <Field label="Hasło administratora (opcjonalnie)">
          <input
            className="input"
            type="password"
            value={matingPassword}
            onChange={(event) => setMatingPassword(event.target.value)}
            data-testid="breeding-mating-password"
            {...passwordFieldProps(`${fieldNs}-breeding-mating-password`)}
          />
        </Field>
        <div className="modal__actions">
          {matingTargets?.length === 1 && matingByEwe.has(matingTargets[0]!) ? (
            <button
              type="button"
              className="btn btn--danger btn--ghost"
              disabled={busy}
              onClick={() => void deleteMating()}
              data-testid="breeding-mating-delete"
            >
              Usuń pokrycie
            </button>
          ) : null}
          <button type="button" className="btn btn--ghost" onClick={closeMatingModal}>
            Anuluj
          </button>
          <button
            className="btn"
            type="submit"
            disabled={busy}
            data-testid="breeding-mating-save"
          >
            Zapisz
          </button>
        </div>
      </FormModal>
    </main>
  );
}
